import type { LevelCatalog, OfflineSeedProvider, SaveRecord, SaveService, SuspendedLevel } from "@highmarch/app";
import type { BalanceData } from "@highmarch/domain";
import type { ProgressKeeper } from "../keeper";
import type { LevelController } from "../level/levelController";
import type { SceneRig } from "../render/scene";
import type { AssetLibrary } from "../render/assets";
import type { HubScene } from "../render/hubScene";
import type { ScreenWakeLock } from "../infra/wakeLock";
import type { StartGuard } from "../ui/levelLoading";
import type { HubView } from "../ui/hub/hubView";
import type { CampaignMap } from "../ui/hub/campaignMap";
import type { TitleScreen } from "../ui/title";
import type { SettingsSheetActions } from "../ui/settings";

/** What the composition root (main.ts) builds once and the flows (hubFlow, levelFlow, metaFlow) share. */
export interface GameServices {
  readonly balance: BalanceData;
  readonly saves: SaveService;
  readonly save: SaveRecord;
  readonly catalog: LevelCatalog;
  /** Suspend/resume, best waves, the away and install cards, the badge and the save backup (keeper.ts). */
  readonly keeper: ProgressKeeper;
  readonly seeds: OfflineSeedProvider;
  readonly rig: SceneRig;
  /** The UI root (`#ui`). */
  readonly ui: HTMLElement;
  /** Keeps the screen on while a level's sim runs (the level says when: start, pause, resume, end). */
  readonly wakeLock: ScreenWakeLock;
  /** `?allhq` (or the readability sheet): load every HQ model at boot and never unload — for captures that stage anything. */
  readonly keepAllHq: boolean;
  readonly version: string;
  /** Taps on PLAY, CONTINUE or a map level while a start is under way are ignored. */
  readonly starts: StartGuard;
}

/** What is on screen: the flows swap these. The asset library arrives once boot has loaded the hub's set. */
export class Screens {
  level: LevelController | null = null;
  hub: HubView | null = null;
  hubScene: HubScene | null = null;
  campaign: CampaignMap | null = null;
  title: TitleScreen | null = null;
  lib: AssetLibrary | null = null;
}

/** The calls the flows make into one another and into the composition root (main.ts `Game` implements it). */
export interface Nav {
  showMenu(): void | Promise<void>;
  /** PLAY or a level on the map: continues a suspended battle of that level, or asks to abandon it first. */
  requestStart(level: number): void;
  continueSuspended(): void;
  startLevel(level: number, resume?: SuspendedLevel | null): Promise<void>;
  /**
   * A newer build is waiting: offers it (a toast with UPDATE) on the hub. Never reloads by itself: a reload at the
   * end of a battle dropped the player on the launch screen with no word of why (M30 #21).
   */
  offerUpdate(): void;
  persist(): void;
  settingsActions(): SettingsSheetActions;
  openGuide(): void;
  resetGuide(): void;
}
